import { useEffect, useState } from "react";

import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  CubeIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";

import usePartAssembly from "../../../../data/usePartAssembly";
import {
  CategoryWithParts,
  ILeadPartsTableGlobalFilters,
  IPartAssembly,
  IPartAssemblyPartCategories,
} from "../../../../models/Part";
import { IVehicle } from "../../../../models/Vehicle";
import classNames from "../../../../utils/classNames";
import PriceFormat from "../../../../utils/priceFormat";
import LoadingWheel from "../../../shared/LoadingWheel";
import LeadPartsCategoriesTable from "./LeadPartsCategoriesTable";

export interface PartAssemblyWithMatchingCategories extends IPartAssembly {
  matchingCategories: CategoryWithParts[];
}

export interface LeadPartsCategoriesProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  handleTableSelectionChange: (
    tableId: string,
    update: CategoryWithParts[] | ((prev: CategoryWithParts[]) => CategoryWithParts[])
  ) => void;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
}

/** Sum the category `averagePrice` values — used for the total-value pill in
 *  each assembly header. Treats missing/zero prices as zero so the pill
 *  always renders even if the backend hasn't populated the field yet. */
function sumAveragePrice(cats: CategoryWithParts[]): number {
  return cats.reduce(
    (sum, cat) => sum + (cat?.category?.averagePrice ?? 0),
    0
  );
}

interface AssemblySectionProps {
  id: string;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  categories: CategoryWithParts[];
  selectedCategories: CategoryWithParts[];
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/** Reusable, stylised header-and-body section used for each assembly (and
 *  the "Non-Assembly Parts" bucket). Rounded card, subtle shadow, clickable
 *  header with selection counts and total value. Mirrors the visual
 *  grammar of the Unlisted Parts / JobAssemblyStats panels so the Create
 *  Jobs screen feels like part of the same family. */
function AssemblySection({
  id,
  name,
  icon: Icon,
  categories,
  selectedCategories,
  collapsed,
  onToggle,
  children,
}: AssemblySectionProps) {
  const total = categories.length;
  const selected = selectedCategories.length;
  const hasSelection = selected > 0;
  const totalValue = sumAveragePrice(categories);
  const selectedValue = sumAveragePrice(selectedCategories);

  return (
    <section
      className={classNames(
        "overflow-hidden rounded-xl border bg-white shadow-sm transition-colors",
        hasSelection
          ? "border-emerald-300 ring-1 ring-emerald-200"
          : "border-slate-200"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={`assembly-body-${id}`}
        className={classNames(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40",
          hasSelection
            ? "bg-emerald-50/60 hover:bg-emerald-50"
            : "bg-slate-50/60 hover:bg-slate-50"
        )}
      >
        <span
          className={classNames(
            "flex h-9 w-9 flex-none items-center justify-center rounded-lg ring-1 ring-inset",
            hasSelection
              ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
              : "bg-slate-100 text-slate-600 ring-slate-200"
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="truncate text-sm font-semibold text-slate-900">
              {name}
            </h3>
            <span
              className={classNames(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset",
                hasSelection
                  ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                  : "bg-slate-100 text-slate-600 ring-slate-200"
              )}
              title={`${selected} of ${total} categories selected`}
            >
              {selected} / {total} selected
            </span>
            {totalValue > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 ring-1 ring-inset ring-blue-200"
                title={
                  hasSelection
                    ? `${PriceFormat(
                        selectedValue
                      )} selected of ${PriceFormat(totalValue)} total`
                    : `${PriceFormat(totalValue)} total average value`
                }
              >
                {hasSelection
                  ? `${PriceFormat(selectedValue)} / ${PriceFormat(totalValue)}`
                  : PriceFormat(totalValue)}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {total === 1 ? "1 category" : `${total} categories`}
            {hasSelection &&
              ` · ${selected} ready for dismantling list`}
          </p>
        </div>

        <ChevronDownIcon
          className={classNames(
            "h-5 w-5 flex-none text-slate-500 transition-transform duration-200",
            collapsed ? "-rotate-90" : "rotate-0"
          )}
          aria-hidden="true"
        />
      </button>

      {!collapsed && (
        <div
          id={`assembly-body-${id}`}
          className="border-t border-slate-200 bg-white"
        >
          <div className="p-3 sm:p-4">{children}</div>
        </div>
      )}
    </section>
  );
}

export default function LeadPartsCategories({
  categories,
  selectedCategories,
  handleTableSelectionChange,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
}: LeadPartsCategoriesProps) {
  const { assemblies } = usePartAssembly();
  const [isLoading, setIsLoading] = useState(true);
  const [categoryAssemblies, setCategoryAssemblies] = useState<
    PartAssemblyWithMatchingCategories[] | undefined
  >([]);
  const [unmatchedCategories, setUnmatchedCategories] = useState<
    CategoryWithParts[] | undefined
  >([]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );

  const getUniqueCategories = (incoming: CategoryWithParts[]) => {
    const byCategoryId = new Map<string, CategoryWithParts>();
    incoming.forEach((category) => {
      if (!byCategoryId.has(category.category.id)) {
        byCategoryId.set(category.category.id, category);
      }
    });
    return [...byCategoryId.values()];
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!assemblies.isLoading && categories) {
      const uniqueCategories = getUniqueCategories(categories);

      //Find matching categories for each assembly and unmatched categories
      const matchedAssemblies: PartAssemblyWithMatchingCategories[] =
        assemblies.data?.data.map((assembly: IPartAssembly) => {
          // Create a map of categoryId to order for quick lookup
          const categoryOrderMap = new Map(
            assembly.partAssemblyPartCategories?.map((cat, index) => [
              cat.partCategoryId,
              index,
            ]) ?? []
          );

          // Filter and sort categories based on the order in partAssemblyPartCategories
          const matchingCategories: CategoryWithParts[] = uniqueCategories
            .filter((category) =>
              assembly.partAssemblyPartCategories?.some(
                (cat: IPartAssemblyPartCategories) =>
                  cat.partCategoryId === category.category.id
              )
            )
            .sort((a, b) => {
              const orderA =
                categoryOrderMap.get(a.category.id) ??
                Number.MAX_SAFE_INTEGER;
              const orderB =
                categoryOrderMap.get(b.category.id) ??
                Number.MAX_SAFE_INTEGER;
              return orderA - orderB;
            });

          return {
            ...assembly,
            matchingCategories,
          };
        }) ?? [];

      // Filter out categories that have no matching assemblies
      const unmatchedCategories = uniqueCategories.filter(
        (category) =>
          !matchedAssemblies?.some((assembly) =>
            assembly.partAssemblyPartCategories?.some(
              (cat: IPartAssemblyPartCategories) =>
                cat.partCategoryId === category.category.id
            )
          )
      );

      setCategoryAssemblies(matchedAssemblies);
      setUnmatchedCategories(unmatchedCategories);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, categories]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  const populatedAssemblies =
    categoryAssemblies?.filter(
      (a) => a.matchingCategories && a.matchingCategories.length > 0
    ) ?? [];

  return (
    <div className="space-y-3">
      {populatedAssemblies.map((matchedAssembly) => {
        const preselectedForThisAssembly =
          matchedAssembly.matchingCategories.filter((cat) =>
            selectedCategories?.some(
              (sel) => sel.category.id === cat.category.id
            )
          );

        return (
          <AssemblySection
            key={matchedAssembly.id}
            id={matchedAssembly.id}
            name={matchedAssembly.name}
            icon={RectangleStackIcon}
            categories={matchedAssembly.matchingCategories}
            selectedCategories={preselectedForThisAssembly}
            collapsed={collapsedSections.has(matchedAssembly.id)}
            onToggle={() => toggleSection(matchedAssembly.id)}
          >
            <LeadPartsCategoriesTable
              categories={matchedAssembly.matchingCategories}
              selectedCategories={preselectedForThisAssembly}
              setSelectedCategories={(update) =>
                handleTableSelectionChange(matchedAssembly.id, update)
              }
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
              vehicle={vehicle}
              filter={filter}
              leadPartsTableFilters={leadPartsTableFilters}
            />
          </AssemblySection>
        );
      })}

      {unmatchedCategories && unmatchedCategories.length > 0 && (() => {
        const preselectedForUnmatched = unmatchedCategories.filter((cat) =>
          selectedCategories?.some(
            (sel) => sel.category.id === cat.category.id
          )
        );

        return (
          <AssemblySection
            id="unmatched"
            name="Non-Assembly Parts"
            icon={AdjustmentsHorizontalIcon}
            categories={unmatchedCategories}
            selectedCategories={preselectedForUnmatched}
            collapsed={collapsedSections.has("unmatched")}
            onToggle={() => toggleSection("unmatched")}
          >
            <LeadPartsCategoriesTable
              categories={unmatchedCategories}
              selectedCategories={preselectedForUnmatched}
              setSelectedCategories={(update) =>
                handleTableSelectionChange("unmatched", update)
              }
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
              vehicle={vehicle}
              filter={filter}
              leadPartsTableFilters={leadPartsTableFilters}
            />
          </AssemblySection>
        );
      })()}

      {populatedAssemblies.length === 0 &&
        (!unmatchedCategories || unmatchedCategories.length === 0) && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500 shadow-sm">
            <CubeIcon
              className="mx-auto mb-2 h-6 w-6 text-slate-300"
              aria-hidden="true"
            />
            No part assemblies match the current filters.
          </div>
        )}
    </div>
  );
}
